"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Noise = void 0;
const x25519_1 = __importDefault(require("bcrypto/lib/js/x25519"));
const buffer_1 = require("buffer");
const it_pb_rpc_1 = __importDefault(require("it-pb-rpc"));
const duplex_1 = __importDefault(require("it-pair/duplex"));
const it_buffer_1 = __importDefault(require("it-buffer"));
const it_pipe_1 = __importDefault(require("it-pipe"));
const it_length_prefixed_1 = require("it-length-prefixed");
const handshake_xx_1 = require("./handshake-xx");
const handshake_ik_1 = require("./handshake-ik");
const handshake_xx_fallback_1 = require("./handshake-xx-fallback");
const utils_1 = require("./utils");
const encoder_1 = require("./encoder");
const crypto_1 = require("./crypto");
const keycache_1 = require("./keycache");
const logger_1 = require("./logger");
const constants_1 = require("./constants");
class Noise {
    /**
     *
     * @param {bytes} staticNoiseKey x25519 private key, reuse for faster handshakes
     * @param {bytes} earlyData
     */
    constructor(staticNoiseKey, earlyData) {
        this.protocol = '/noise';
        this.prologue = buffer_1.Buffer.alloc(0);
        this.earlyData = earlyData || buffer_1.Buffer.alloc(0);
        // disabled until properly specked
        this.useNoisePipes = false;
        if (staticNoiseKey) {
            const publicKey = x25519_1.default.publicKeyCreate(staticNoiseKey);
            this.staticKeys = {
                privateKey: staticNoiseKey,
                publicKey
            };
        }
        else {
            this.staticKeys = utils_1.generateKeypair();
        }
    }
    /**
     * Encrypt outgoing data to the remote party (handshake as initiator)
     * @param {PeerId} localPeer - PeerId of the receiving peer
     * @param {any} connection - streaming iterable duplex that will be encrypted
     * @param {PeerId} remotePeer - PeerId of the remote peer. Used to validate the integrity of the remote peer.
     * @returns {Promise<SecureOutbound>}
     */
    async secureOutbound(localPeer, connection, remotePeer) {
        const wrappedConnection = it_pb_rpc_1.default(connection, {
            // wrong types in repo
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            lengthEncoder: encoder_1.uint16BEEncode,
            lengthDecoder: encoder_1.uint16BEDecode,
            maxDataLength: constants_1.NOISE_MSG_MAX_LENGTH_BYTES
        });
        const handshake = await this.performHandshake({
            connection: wrappedConnection,
            isInitiator: true,
            localPeer,
            remotePeer
        });
        const conn = await this.createSecureConnection(wrappedConnection, handshake);
        return {
            conn,
            remoteEarlyData: handshake.remoteEarlyData,
            remotePeer: handshake.remotePeer
        };
    }
    /**
     * Decrypt incoming data (handshake as responder).
     * @param {PeerId} localPeer - PeerId of the receiving peer.
     * @param {any} connection - streaming iterable duplex that will be encryption.
     * @param {PeerId} remotePeer - optional PeerId of the initiating peer, if known. This may only exist during transport upgrades.
     * @returns {Promise<SecureOutbound>}
     */
    async secureInbound(localPeer, connection, remotePeer) {
        const wrappedConnection = it_pb_rpc_1.default(connection, {
            // wrong types in repo
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            lengthEncoder: encoder_1.uint16BEEncode,
            lengthDecoder: encoder_1.uint16BEDecode,
            maxDataLength: constants_1.NOISE_MSG_MAX_LENGTH_BYTES
        });
        const handshake = await this.performHandshake({
            connection: wrappedConnection,
            isInitiator: false,
            localPeer,
            remotePeer
        });
        const conn = await this.createSecureConnection(wrappedConnection, handshake);
        return {
            conn,
            remoteEarlyData: handshake.remoteEarlyData,
            remotePeer: handshake.remotePeer
        };
    }
    /**
     * If Noise pipes supported, tries IK handshake first with XX as fallback if it fails.
     * If noise pipes disabled or remote peer static key is unknown, use XX.
     * @param {HandshakeParams} params
     */
    async performHandshake(params) {
        const payload = await utils_1.getPayload(params.localPeer, this.staticKeys.publicKey, this.earlyData);
        let tryIK = this.useNoisePipes;
        if (params.isInitiator && keycache_1.KeyCache.load(params.remotePeer) === null) {
            // if we are initiator and remote static key is unknown, don't try IK
            tryIK = false;
        }
        // Try IK if acting as responder or initiator that has remote's static key.
        if (tryIK) {
            // Try IK first
            const { remotePeer, connection, isInitiator } = params;
            const ikHandshake = new handshake_ik_1.IKHandshake(isInitiator, payload, this.prologue, this.staticKeys, connection, 
            // safe to cast as we did checks
            keycache_1.KeyCache.load(params.remotePeer) || buffer_1.Buffer.alloc(32), remotePeer);
            try {
                return await this.performIKHandshake(ikHandshake);
            }
            catch (e) {
                // IK failed, go to XX fallback
                let ephemeralKeys;
                if (params.isInitiator) {
                    ephemeralKeys = ikHandshake.getLocalEphemeralKeys();
                }
                return await this.performXXFallbackHandshake(params, payload, e.initialMsg, ephemeralKeys);
            }
        }
        else {
            // run XX handshake
            return await this.performXXHandshake(params, payload);
        }
    }
    async performXXFallbackHandshake(params, payload, initialMsg, ephemeralKeys) {
        const { isInitiator, remotePeer, connection } = params;
        const handshake = new handshake_xx_fallback_1.XXFallbackHandshake(isInitiator, payload, this.prologue, this.staticKeys, connection, initialMsg, remotePeer, ephemeralKeys);
        try {
            await handshake.propose();
            await handshake.exchange();
            await handshake.finish();
        }
        catch (e) {
            logger_1.logger(e);
            throw new Error(`Error occurred during XX Fallback handshake: ${e.message}`);
        }
        return handshake;
    }
    async performXXHandshake(params, payload) {
        const { isInitiator, remotePeer, connection } = params;
        const handshake = new handshake_xx_1.XXHandshake(isInitiator, payload, this.prologue, this.staticKeys, connection, remotePeer);
        try {
            await handshake.propose();
            await handshake.exchange();
            await handshake.finish();
            if (this.useNoisePipes && handshake.remotePeer) {
                keycache_1.KeyCache.store(handshake.remotePeer, handshake.getRemoteStaticKey());
            }
        }
        catch (e) {
            throw new Error(`Error occurred during XX handshake: ${e.message}`);
        }
        return handshake;
    }
    async performIKHandshake(handshake) {
        await handshake.stage0();
        await handshake.stage1();
        return handshake;
    }
    async createSecureConnection(connection, handshake) {
        // Create encryption box/unbox wrapper
        const [secure, user] = duplex_1.default();
        const network = connection.unwrap();
        await it_pipe_1.default(secure, // write to wrapper
        it_buffer_1.default, // ensure any type of data is converted to buffer
        crypto_1.encryptStream(handshake), // data is encrypted
        it_length_prefixed_1.encode({ lengthEncoder: encoder_1.uint16BEEncode }), // prefix with message length
        network, // send to the remote peer
        it_length_prefixed_1.decode({ lengthDecoder: encoder_1.uint16BEDecode }), // read message length prefix
        it_buffer_1.default, // ensure any type of data is converted to buffer
        crypto_1.decryptStream(handshake), // decrypt the incoming data
        secure // pipe to the wrapper
        );
        return user;
    }
}
exports.Noise = Noise;
//# sourceMappingURL=noise.js.map